import { StyleSheet, Platform } from 'react-native'

const C = {
  oddsBg: '#0F0A1F',
  oddsBorder: '#2A1F44',
  primary: '#A78BFA',
  amber: '#F59E0B',
  textDefault: '#F7FAFC',
  textDim: '#9CA3AF',
  textMuted: '#6B7280',
  textDark: '#111827',
  green: '#6EE7B7',
} as const

export const getInlineMarketPickerStyles = () =>
  StyleSheet.create({
    container: {
      gap: 8,
    },
    marketDesc: {
      fontSize: 11,
      color: C.textMuted,
      fontWeight: '500' as const,
    },
    // ── Market chip strip ───────────────────────────────────────
    chipsRow: {
      flexDirection: 'row' as const,
      gap: 4,
    },
    chip: {
      paddingHorizontal: 11,
      paddingVertical: 6,
      borderRadius: 9999,
      borderWidth: 1,
      borderColor: C.oddsBorder,
      flexShrink: 0,
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 4,
    },
    chipCrown: {
      width: 10,
      height: 10,
      tintColor: C.amber,
    },
    chipCrownSelected: {
      tintColor: C.textDark,
    },
    chipDimmed: {
      opacity: 0.5,
    },
    rowDimmed: {
      opacity: 0.45,
    },
    ouLineCellRow: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 3,
    },
    ouRowCrown: {
      width: 9,
      height: 9,
      tintColor: C.amber,
    },
    chipSelected: {
      backgroundColor: C.primary,
      borderWidth: 0,
    },
    chipPressed: {
      opacity: 0.75,
    },
    // A pick already sits on this bet type (specs/challenge-flow-redesign AC-3.10).
    chipPickedDot: {
      width: 6,
      height: 6,
      borderRadius: 9999,
      backgroundColor: C.primary,
    },
    chipPickedDotSelected: {
      backgroundColor: C.textDark,
    },
    chipText: {
      fontSize: 12,
      fontWeight: '600' as const,
      color: C.textDim,
    },
    chipTextSelected: {
      color: C.textDark,
      fontWeight: '700' as const,
    },
    // ── Standard odds tiles (1X2, BTTS, Double Chance, etc.) ───
    tilesRow: {
      flexDirection: 'row',
      gap: 6,
    },
    tile: {
      flex: 1,
      paddingVertical: 9,
      paddingHorizontal: 6,
      borderRadius: 10,
      backgroundColor: C.oddsBg,
      borderWidth: 1,
      borderColor: C.oddsBorder,
      alignItems: 'center',
      gap: 1,
      overflow: 'hidden',
      position: 'relative',
      minHeight: 54,
    },
    tileSelected: {
      backgroundColor: C.primary,
      borderWidth: 0,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 2px 8px rgba(167,139,250,0.4)' as const }
        : {}),
    },
    tilePressed: {
      opacity: 0.75,
    },
    tileLabel: {
      fontSize: 10,
      fontWeight: '700' as const,
      letterSpacing: 0.4,
      color: C.textDim,
      textTransform: 'uppercase' as const,
    },
    tileLabelSelected: {
      color: 'rgba(17,24,39,0.65)',
    },
    tileSub: {
      fontSize: 9,
      fontWeight: '600' as const,
      color: C.textMuted,
    },
    tileSubSelected: {
      color: 'rgba(17,24,39,0.55)',
    },
    tileOdds: {
      fontSize: 17,
      fontWeight: '700' as const,
      color: C.amber,
      fontVariant: ['tabular-nums'],
    },
    tileOddsSelected: {
      color: C.textDark,
    },
    communityBar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: 2,
      backgroundColor: C.green,
      opacity: 0.5,
    },
    // ── O/U grid (Goals, 1H O/U, Corners) ────────────────────
    ouGrid: {
      gap: 4,
    },
    ouHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    ouLineSpacer: {
      width: 52,
    },
    ouHeaderCell: {
      flex: 1,
      alignItems: 'center',
    },
    ouHeaderText: {
      fontSize: 9,
      fontWeight: '700' as const,
      color: C.textMuted,
      letterSpacing: 0.6,
      textTransform: 'uppercase' as const,
    },
    ouDataRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    ouLineCell: {
      width: 52,
      alignItems: 'center',
    },
    ouLineText: {
      fontSize: 11,
      fontWeight: '700' as const,
      color: C.textDefault,
      fontVariant: ['tabular-nums'],
    },
    ouCell: {
      flex: 1,
      paddingVertical: 7,
      paddingHorizontal: 4,
      borderRadius: 8,
      backgroundColor: C.oddsBg,
      borderWidth: 1,
      borderColor: C.oddsBorder,
      alignItems: 'center',
      minHeight: 34,
    },
    ouCellSelected: {
      backgroundColor: C.primary,
      borderWidth: 0,
    },
    ouCellText: {
      fontSize: 13,
      fontWeight: '700' as const,
      color: C.amber,
      fontVariant: ['tabular-nums'],
    },
    ouCellTextSelected: {
      color: C.textDark,
    },
    // ── Scrollable chip strip for many-value markets ──────────
    manyChipsRow: {
      flexDirection: 'row',
      gap: 5,
    },
    manyChip: {
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 9999,
      borderWidth: 1,
      borderColor: C.oddsBorder,
      backgroundColor: C.oddsBg,
      alignItems: 'center',
      flexDirection: 'row',
      gap: 5,
      flexShrink: 0,
      minHeight: 36,
    },
    manyChipSelected: {
      backgroundColor: C.primary,
      borderWidth: 0,
    },
    manyChipLabel: {
      fontSize: 13,
      fontWeight: '700' as const,
      color: C.textDefault,
      fontVariant: ['tabular-nums'],
    },
    manyChipLabelSelected: {
      color: C.textDark,
    },
    manyChipOdds: {
      fontSize: 10,
      fontWeight: '600' as const,
      color: C.amber,
      fontVariant: ['tabular-nums'],
    },
    manyChipOddsSelected: {
      color: 'rgba(17,24,39,0.6)',
    },
    // ── Handicap multi-line ───────────────────────────────────
    hcapBlock: {
      gap: 6,
    },
    hcapLineRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      marginBottom: 4,
    },
    hcapLineTag: {
      fontSize: 9,
      fontWeight: '700' as const,
      color: C.textMuted,
      letterSpacing: 0.6,
      textTransform: 'uppercase' as const,
    },
    hcapLinePill: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 9999,
      backgroundColor: 'rgba(167,139,250,0.15)',
      borderWidth: 1,
      borderColor: 'rgba(167,139,250,0.4)',
    },
    hcapLinePillText: {
      fontSize: 11,
      fontWeight: '700' as const,
      color: C.primary,
    },
  })
